import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * HomePreApprovedGateway
 *
 * Landing page for /home/preapproved?bap=PetPooja
 *
 * Shows a branded animated splash screen for ~2 seconds, then performs a
 * client-side redirect to /v2/home/preapproved preserving all query params.
 *
 * This is intentionally a hard window.location redirect (not React Router
 * navigate) so the v2 page receives a clean mount with the full query string
 * and no stale router state.
 */

const REDIRECT_DELAY_MS = 2200;

// Animated dots for the loading indicator
const BouncingDots: React.FC = () => (
  <div className="flex items-center justify-center gap-2" aria-label="Loading">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: "#F5A623",
          animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
        }}
      />
    ))}
  </div>
);

// Animated circular progress ring
const ProgressRing: React.FC<{ progress: number }> = ({ progress }) => {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg width="128" height="128" viewBox="0 0 128 128" style={{ transform: "rotate(-90deg)" }}>
      {/* Track */}
      <circle
        cx="64"
        cy="64"
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.15)"
        strokeWidth="8"
      />
      {/* Progress arc */}
      <circle
        cx="64"
        cy="64"
        r={radius}
        fill="none"
        stroke="#F5A623"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        style={{ transition: "stroke-dashoffset 0.1s linear" }}
      />
    </svg>
  );
};

const HomePreApprovedGateway: React.FC = () => {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  // Fade in on mount
  useEffect(() => {
    const t = setTimeout(() => setFadeIn(true), 50);
    return () => clearTimeout(t);
  }, []);

  // Progress bar ticker
  useEffect(() => {
    const step = 100 / (REDIRECT_DELAY_MS / 50);
    const interval = setInterval(() => {
      setProgress((p) => Math.min(p + step, 100));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Fade out then redirect
  useEffect(() => {
    const fadeTimer = setTimeout(() => setFadeOut(true), REDIRECT_DELAY_MS - 400);
    const redirectTimer = setTimeout(() => {
      window.location.replace(`/v2/home/preapproved${location.search}`);
    }, REDIRECT_DELAY_MS);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(redirectTimer);
    };
  }, [location.search]);

  const bap = new URLSearchParams(location.search).get("bap") ?? "";

  return (
    <>
      {/* Keyframe animations injected inline — no extra CSS file needed */}
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
          40%            { transform: scale(1.0); opacity: 1;   }
        }
        @keyframes floatUp {
          0%   { transform: translateY(0px);  }
          50%  { transform: translateY(-8px); }
          100% { transform: translateY(0px);  }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position:  400px 0; }
        }
        @keyframes pulseRing {
          0%   { transform: scale(0.95); box-shadow: 0 0 0 0   rgba(245,166,35,0.5); }
          70%  { transform: scale(1);    box-shadow: 0 0 0 18px rgba(245,166,35,0);   }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0   rgba(245,166,35,0);   }
        }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(160deg, #1a4731 0%, #0d2b1e 60%, #0a1f16 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 16px",
          opacity: fadeIn && !fadeOut ? 1 : 0,
          transition: "opacity 0.4s ease",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background circles */}
        <div style={{
          position: "absolute", top: "-80px", right: "-80px",
          width: 320, height: 320, borderRadius: "50%",
          background: "rgba(245,166,35,0.06)", pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: "-60px", left: "-60px",
          width: 240, height: 240, borderRadius: "50%",
          background: "rgba(245,166,35,0.04)", pointerEvents: "none",
        }} />

        {/* Card */}
        <div
          style={{
            background: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 24,
            padding: "40px 32px",
            maxWidth: 380,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 28,
            boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
          }}
        >
          {/* Progress ring + logo */}
          <div style={{ position: "relative", width: 128, height: 128 }}>
            <ProgressRing progress={progress} />
            {/* Intellend "I" mark centred inside the ring */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "floatUp 2.4s ease-in-out infinite",
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #2d7a4f 0%, #1a4731 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "pulseRing 2s ease-out infinite",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                }}
              >
                <span style={{
                  color: "#F5A623",
                  fontSize: 28,
                  fontWeight: 900,
                  fontFamily: "system-ui, sans-serif",
                  letterSpacing: "-1px",
                }}>
                  i
                </span>
              </div>
            </div>
          </div>

          {/* Headline */}
          <div style={{ textAlign: "center" }}>
            <h1 style={{
              color: "#ffffff",
              fontSize: 22,
              fontWeight: 800,
              margin: 0,
              lineHeight: 1.3,
              fontFamily: "system-ui, sans-serif",
            }}>
              🎉 Pre-approved Offer
            </h1>
            {bap && (
              <p style={{
                color: "rgba(255,255,255,0.55)",
                fontSize: 13,
                marginTop: 6,
                fontFamily: "system-ui, sans-serif",
              }}>
                via <span style={{ color: "#F5A623", fontWeight: 600 }}>{bap}</span>
              </p>
            )}
          </div>

          {/* Shimmer text */}
          <p
            style={{
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "system-ui, sans-serif",
              background: "linear-gradient(90deg, rgba(255,255,255,0.4) 25%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0.4) 75%)",
              backgroundSize: "400px 100%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              animation: "shimmer 1.8s linear infinite",
              margin: 0,
            }}
          >
            Preparing your offer…
          </p>

          {/* Bouncing dots */}
          <BouncingDots />

          {/* Progress bar */}
          <div style={{
            width: "100%",
            height: 4,
            background: "rgba(255,255,255,0.1)",
            borderRadius: 99,
            overflow: "hidden",
          }}>
            <div style={{
              height: "100%",
              width: `${progress}%`,
              background: "linear-gradient(90deg, #2d7a4f, #F5A623)",
              borderRadius: 99,
              transition: "width 0.1s linear",
            }} />
          </div>

          {/* Tagline */}
          <p style={{
            color: "rgba(255,255,255,0.35)",
            fontSize: 11,
            margin: 0,
            fontFamily: "system-ui, sans-serif",
            textAlign: "center",
          }}>
            Powered by <span style={{ color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>Intellend</span>
          </p>
        </div>
      </div>
    </>
  );
};

export default HomePreApprovedGateway;
